import { useQuery } from '@tanstack/react-query'
import { DisplayableUser, getCurrentUser } from './model'
import { data, UserApi } from '@portal/pkg-api/shared'
import { useMemo } from 'react'
import { useEffectiveUser } from './UserInfo'
import { useUserDisplayName } from './useUserDisplayName'
import { UserBasic } from '@portal/pkg-api/gen/auth'
import { t } from 'i18next'

const prefix = 'shared.user'

export const UserQueries = {
  users: () => ({
    queryKey: [`${prefix}.users`],
    queryFn: () => data(UserApi.getUsers()),
  }),
  sortedDisplayableUsers: () => ({
    queryKey: [`${prefix}.sortedDisplayableUsers`],
    queryFn: sortedDisplayableUsersQuery,
  }),
  activeUsers: () => ({
    queryKey: [`${prefix}.activeUsers`],
    queryFn: async () => {
      const users = await data(UserApi.getUsers())
      return users.filter((u) => u.active && u.enabled)
    },
  }),
}

async function sortedDisplayableUsersQuery(): Promise<Array<DisplayableUser>> {
  const users = await data(UserApi.getUsers())
  return users
    .map((u) => ({
      ...u,
      identifier: u.id.toString(),
      displayName: t('displayName', { familyName: u.familyName, givenName: u.givenName }),
    }))
    .sort((a, b) => {
      const enabledDiff = Number(b.enabled) - Number(a.enabled)
      if (enabledDiff !== 0) {
        return enabledDiff
      }
      return a.displayName.localeCompare(b.displayName)
    })
}

export function useUsers() {
  return useQuery<ReadonlyArray<UserBasic>>(UserQueries.users())
}

export function useMakeDisplayableUsers(users: ReadonlyArray<UserBasic> = []) {
  const displayName = useUserDisplayName()

  return useMemo<ReadonlyArray<DisplayableUser>>(
    () =>
      users
        .map((u) => ({ ...u, identifier: u.id.toString(), displayName: displayName(u) }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [users, displayName],
  )
}

export function useDisplayableUsers() {
  const { data: users = [] } = useUsers()
  return useMakeDisplayableUsers(users)
}

export function useUsersLookup(extraUsers: DisplayableUser[] = []): {
  [key: string]: DisplayableUser
} {
  const users = useDisplayableUsers()

  return useMemo(
    () => extraUsers.concat(users).reduce((val, u) => ({ ...val, [u.identifier]: u }), {}),
    [extraUsers, users],
  )
}

/**
 * Convenience hook for converting comma-delimited list of user ids to array of users.
 *
 * @param value Comma-delimited list of user ids.
 * @param extraUsers Any users to prepend to the
 */
export function useUsersValue(value: string = '', extraUsers: DisplayableUser[] = []) {
  const displayName = useUserDisplayName()
  value = value || ''

  const effectiveUser = useEffectiveUser()
  const userLookup = useUsersLookup(extraUsers)

  return useMemo(
    () =>
      value
        .split(/,\s*/)
        .filter(userLookup.hasOwnProperty.bind(userLookup))
        .map((id) => userLookup[id])
        .map((u) => ({
          ...u,
          displayName: displayName(u),
        }))
        .sort(userSortFn(effectiveUser?.id)),
    [value, userLookup, effectiveUser?.id, displayName],
  )
}

/**
 * Sort order: CURRENT_USER, LOGGED_IN_USER, familyName, givenName, id
 */
const userSortFn = (currentUserId?: number) => {
  const missingSortIndex = 100
  const sortIndexFor = (u: DisplayableUser) => {
    if (u === getCurrentUser()) {
      return 1
    }
    if (currentUserId !== undefined && u.identifier === currentUserId.toString()) {
      return 2
    }
    return missingSortIndex
  }

  return (a: DisplayableUser, b: DisplayableUser) => {
    const aSortIndex = sortIndexFor(a)
    const bSortIndex = sortIndexFor(b)

    if (aSortIndex === missingSortIndex && bSortIndex === missingSortIndex) {
      return `${a.familyName}${a.givenName}${a.identifier}`.localeCompare(
        `${b.familyName}${b.givenName}${b.identifier}`,
      )
    }

    return aSortIndex - bSortIndex
  }
}
